import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

const API = process.env.REACT_APP_API_BASE ?? "";

async function getJSON(path, opts = {}) {
  const res = await fetch(`${API}${path}`, {
    credentials: "include",
    headers: { Accept: "application/json", ...(opts.headers || {}) },
    ...opts,
  });
  if (!res.ok) throw new Error(`HTTP ${res.status} ${path}`);
  const ct = res.headers.get("content-type") || "";
  return ct.includes("application/json") ? res.json() : null;
}

const INPUT_LIGHT =
  "bg-white/90 border border-white/40 text-slate-900 placeholder-slate-500 caret-slate-900 focus:outline-none focus:ring-2 focus:ring-purple-400 rounded-xl";

export default function CinemaMovieManager() {
  const navigate = useNavigate();

  const [cinemas, setCinemas] = useState([]);
  const [movies, setMovies] = useState([]);
  const [selectedCinema, setSelectedCinema] = useState("");

  const [showtimes, setShowtimes] = useState([]); // raw showtimes for the cinema
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);

  // load base data
  useEffect(() => {
    (async () => {
      const [cs, ms] = await Promise.all([
        getJSON("/api/cinemas").catch(() => []),
        getJSON("/api/movies").catch(() => []),
      ]);
      setCinemas(Array.isArray(cs) ? cs : []);
      setMovies(Array.isArray(ms) ? ms : []);
      if (Array.isArray(cs) && cs.length) {
        setSelectedCinema(String(cs[0].cinema_id ?? cs[0].CINEMA_ID));
      }
    })().catch(console.error);
  }, []);

  // load showtimes for chosen cinema
  useEffect(() => {
    if (!selectedCinema) { setShowtimes([]); return; }
    (async () => {
      setLoading(true);
      try {
        const data = await getJSON(`/api/showtimes/${selectedCinema}`).catch(() => []);
        setShowtimes(Array.isArray(data) ? data : []);
      } finally {
        setLoading(false);
      }
    })();
  }, [selectedCinema]);

  // unique movie ids from showtimes
  const movieIds = useMemo(() => {
    const s = new Set();
    for (const st of showtimes) {
      const id = Number(st.movie_id ?? st.MOVIE_ID);
      if (id) s.add(id);
    }
    return Array.from(s);
  }, [showtimes]);

  // resolve to full movie rows (join with /api/movies)
  const lineup = useMemo(() => {
    const rows = movieIds
      .map((id) => movies.find((m) => Number(m.MOVIE_ID ?? m.movie_id) === id))
      .filter(Boolean)
      .sort((a, b) => String(a.MOVIE_NAME).localeCompare(String(b.MOVIE_NAME), "bg"));
    const text = q.toLowerCase().trim();
    return text ? rows.filter((m) => (m.MOVIE_NAME || "").toLowerCase().includes(text)) : rows;
  }, [movieIds, movies, q]);

  return (
    <div className="min-h-screen relative p-4 md:p-6 [color-scheme:light]">
      {/* bg */}
      <div className="fixed inset-0 -z-10 overflow-hidden">
        <img
          src="https://latonacinema.com/wp-content/uploads/2025/06/cinema-hall-with-rows-of-grey-seats-in-darkness.jpg"
          alt="фон"
          className="h-full w-full object-cover blur-[2px] scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#2c204f]/80 via-black/60 to-[#6c56b5]/70" />
      </div>

      {/* header */}
      <div className="max-w-7xl mx-auto flex flex-wrap items-center gap-3 mb-4">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-white/10 text-white border border-white/20 hover:bg-white/15 focus:outline-none focus:ring-2 focus:ring-purple-400/60"
          title="Назад"
        >
          ← Назад
        </button>
        <div className="flex items-center gap-3">
          <img
            src="https://latonacinema.com/wp-content/uploads/2025/06/cropped-latonacinema.png"
            alt="Лого"
            className="h-10"
          />
          <h1 className="text-2xl md:text-3xl font-extrabold text-white tracking-tight">
            🎞️ Листинг по кино (от прожекции)
          </h1>
        </div>
      </div>

      {/* card */}
      <div className="max-w-7xl mx-auto bg-white/8 backdrop-blur-xl border border-white/10 rounded-2xl shadow-2xl p-4 md:p-6 text-white">
        {/* toolbar */}
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <select
            value={selectedCinema}
            onChange={(e) => setSelectedCinema(e.target.value)}
            className={`px-3 py-2 ${INPUT_LIGHT}`}
          >
            {cinemas.map((c) => (
              <option key={c.cinema_id ?? c.CINEMA_ID} value={String(c.cinema_id ?? c.CINEMA_ID)}>
                {c.cinema_name ?? c.CINEMA_NAME}
              </option>
            ))}
          </select>

          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Търси заглавие…"
            className={`px-3 py-2 ${INPUT_LIGHT}`}
          />

          <button
            onClick={() => navigate("/admin/showtimes")}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-blue-600 hover:bg-blue-700 text-white"
            title="Към добавяне на прожекции"
          >
            ➕ Добави прожекции
          </button>

          {loading && <span className="text-white/70 text-sm">Зареждане…</span>}
        </div>

        {/* empty state */}
        {showtimes.length === 0 && (
          <div className="p-4 rounded-xl bg-white/10 border border-white/15">
            Няма прожекции за избраното кино.
            <button
              onClick={() => navigate("/admin/showtimes")}
              className="ml-3 px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white"
            >
              ➕ Добави прожекции
            </button>
          </div>
        )}

        {/* lineup grid */}
        {showtimes.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {lineup.map((m) => (
              <div key={m.MOVIE_ID ?? m.movie_id} className="overflow-hidden rounded-2xl bg-white/5 border border-white/10 shadow-md">
                <img
                  src={m.POSTER_URL || "https://via.placeholder.com/300x450?text=No+Poster"}
                  alt={m.MOVIE_NAME}
                  className="w-full h-64 object-cover"
                />
                <div className="p-3 text-white">
                  <div className="font-semibold leading-snug line-clamp-2 min-h-[2.5rem]">
                    {m.MOVIE_NAME}
                  </div>
                  <button
                    className="mt-2 w-full px-3 py-1.5 rounded-lg bg-white/10 border border-white/20 hover:bg-white/15"
                    onClick={() => navigate("/admin/showtimes")}
                  >
                    🗓 Управление на прожекции
                  </button>
                </div>
              </div>
            ))}

            {!lineup.length && (
              <div className="col-span-full p-4 rounded-xl bg-white/10 border border-white/15 text-center">
                Няма заглавия по филтъра.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
